import { z } from 'zod';

import {
  buildQueryString,
  ListingKind,
  ListingPictureKind,
  makeEndpoint,
  MediaLinkSchema,
  MediaType,
  paginateResponseSchema,
  ProductLine,
  PromotedListingStatus,
  PromoterAgreementStatus,
  PromoterPermissionGrant,
  RequestToPublish,
  ResourceGroupCollectionEventType,
  UpdatePromotedListingCommissionRequestSchema,
  UpdatePromotedListingCommissionResponseSchema,
} from '../..';

export const PromoterPermissionSchema = z.object({
  grants: z.array(z.nativeEnum(PromoterPermissionGrant)),
});

export const FindApprovedPromotersByBrandResponseSchema = z.array(
  z.object({
    promoterBrandId: z.string().uuid(),
    name: z.string(),
  }),
);

export const FindApprovedPromotersByBrandEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/promoters/approved-promoters-by-brand/${brandId}`,
  responseSchema: FindApprovedPromotersByBrandResponseSchema,
  requestSchema: z.void(),
});

const UpdateProviderListingStatusSchema = z.object({
  promoterBrandId: z.string().uuid(),
  isApproved: z.boolean(),
  providerUserId: z.string().uuid(),
  permissions: PromoterPermissionSchema.optional(),
});

export const UpdateProviderListingStatusEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `v1/providers/${brandId}/listings/${listingId}/update-approval`,
  responseSchema: z.void(),
  requestSchema: UpdateProviderListingStatusSchema,
});

const UpdatePromoterListingStatusSchema = z.object({
  isApproved: z.boolean(),
  promoterUserId: z.string().uuid(),
});

export const UpdatePromoterListingStatusEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ promoterBrandId, promotedListingId }: { promoterBrandId: string; promotedListingId: string }) =>
    `v1/promoters/${promoterBrandId}/promoted-listings/${promotedListingId}/update-approval`,
  responseSchema: z.void(),
  requestSchema: UpdatePromoterListingStatusSchema,
});

const FindApprovedPromotedIdsByBrandSchema = z.object({
  promoterBrandId: z.string().nullable(),
  promotedListingId: z.string().uuid().nullable(),
  listingId: z.string().uuid().nullable(),
});

export const FindApprovedPromotedIdsByBrandEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/providers/${brandId}/promoter-ids`,
  responseSchema: z.array(FindApprovedPromotedIdsByBrandSchema),
  requestSchema: z.void(),
});

const FindActivePromoterAgreementSchema = z.object({
  id: z.string().uuid(),
  promoterName: z.string(),
  promoterBrandId: z.string().uuid(),
  isPublicChannel: z.boolean(),
  status: z.nativeEnum(PromoterAgreementStatus),
  providerOrganizationId: z.string().uuid().nullable(),
  providerBrandId: z.string().uuid().nullable(),
  providerListingId: z.string().uuid().nullable(),
  percentCommission: z.number().nullable(),
  flatCommission: z.number().nullable(),
  currency: z.string().nullable(),
  agreementStartDate: z.date().nullable(),
  agreementEndDate: z.date().nullable(),
});

export const FindActivePromoterAgreementEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, promoterBrandId }: { brandId: string; promoterBrandId: string }) =>
    `v1/providers/${brandId}/promoter-brands/${promoterBrandId}/agreement`,
  responseSchema: FindActivePromoterAgreementSchema.nullable(),
  requestSchema: z.void(),
});

const FindActivePromoterAgreementsSchema = z.array(FindActivePromoterAgreementSchema);

export const FindActivePromoterAgreementsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/providers/${brandId}/promoter-agreements`,
  responseSchema: FindActivePromoterAgreementsSchema,
  requestSchema: z.void(),
});

const ProviderListingStatusSchema = z.array(
  z.object({
    promoterName: z.string(),
    promoterBrandId: z.string().uuid(),
    providerBrandId: z.string().uuid(),
    promoterLogoUri: z.string().nullable(),
    percentCommission: z.number(),
    flatCommission: z.number(),
    providerOrganizationId: z.string().uuid().nullable(),
    promotedListingId: z.string().uuid().nullable().optional(),
    listingId: z.string().uuid().nullable().optional(),
    status: z.nativeEnum(PromotedListingStatus).nullable().optional(),
    permissions: PromoterPermissionSchema.optional(),
  }),
);

export const GetProviderListingStatusEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `v1/providers/${brandId}/listings/${listingId}/status`,
  responseSchema: ProviderListingStatusSchema,
  requestSchema: z.void(),
});

const promoterListingStatusObject = z.object({
  listingId: z.string().uuid(),
  providerBrandId: z.string().uuid(),
  promotedListingId: z.string().uuid(),
  promotedListingStatus: z.nativeEnum(PromotedListingStatus),
});

const promoterListingStatusResponse = z.array(promoterListingStatusObject);

export const GetPromoterListingStatusEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ promoterBrandId }: { promoterBrandId: string }) => `v1/promoters/${promoterBrandId}/listing-status`,
  responseSchema: promoterListingStatusResponse,
  requestSchema: z.void(),
});

const CreatePromoterAgreementSchema = z
  .object({
    providerOrganizationId: z.string().uuid().optional(),
    providerBrandId: z.string().uuid().optional(),
    providerListingId: z.string().uuid().optional(),
    percentCommission: z.number().min(0).max(100),
    flatCommission: z.number().min(0).optional(),
  })
  .refine(
    (data) => [data.providerOrganizationId, data.providerBrandId, data.providerListingId].filter(Boolean).length === 1,
    {
      message: 'Exactly one of providerOrganizationId, providerBrandId, or providerListingId must be provided',
      path: ['providerOrganizationId', 'providerBrandId', 'providerListingId'],
    },
  );

const CreatePromoterAgreementResponseSchema = z.object({
  id: z.string().uuid(),
});

export const CreatePromoterAgreementEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ promoterBrandId }: { promoterBrandId: string }) => `v1/promoters/${promoterBrandId}/promoter-agreement`,
  responseSchema: CreatePromoterAgreementResponseSchema,
  requestSchema: CreatePromoterAgreementSchema,
});

const CreatePromoterAgreementsForAllOrgBrandsResponseSchema = z.object({
  agreementsCreated: z.number(),
  promotersCreated: z.number(),
});

export const CreatePromoterAgreementForAllOrgBrandsEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId }: { organizationId: string }) =>
    `v1/promoters/organizations/${organizationId}/promoter-agreement-for-all-brands`,
  responseSchema: CreatePromoterAgreementsForAllOrgBrandsResponseSchema,
  requestSchema: z.void(),
});

const UpdatePromoterAgreementApprovalResponseSchema = z.object({
  id: z.string().uuid(),
});

const UpdatePromoterAgreementApprovalSchema = z.object({
  status: z.nativeEnum(PromoterAgreementStatus),
  updatedByUserId: z.string().uuid(),
});

export const UpdateBrandPromoterAgreementApprovalEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, agreementId }: { brandId: string; agreementId: string }) =>
    `v1/promoters/${brandId}/promoter-agreement/${agreementId}/update-approval`,
  responseSchema: UpdatePromoterAgreementApprovalResponseSchema,
  requestSchema: UpdatePromoterAgreementApprovalSchema,
});

export const UpdatePromotedListingCommissionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `v1/providers/${brandId}/listings/${listingId}/update-commission`,
  requestSchema: UpdatePromotedListingCommissionRequestSchema,
  responseSchema: UpdatePromotedListingCommissionResponseSchema,
});

export const FindPaginatedListingsForBrandSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  promotedListingId: z.string().uuid(),
  promoterListingId: z.string().uuid().optional().nullable(),
  providerBrandId: z.string().uuid(),
  promoterBrandId: z.string().uuid(),
  experienceId: z.string().uuid().optional().nullable(),
  resourceGroupCollectionId: z.string().uuid().optional().nullable(),
  kind: z.nativeEnum(ListingKind),
  productLine: z.nativeEnum(ProductLine),
  eventType: z.nativeEnum(ResourceGroupCollectionEventType).nullable(),
  promotedListingStatus: z.nativeEnum(PromotedListingStatus),
  medias: z.array(
    z.object({
      id: z.string().uuid(),
      kind: z.nativeEnum(ListingPictureKind),
      type: z.nativeEnum(MediaType),
      links: z.array(MediaLinkSchema),
    }),
  ),
});

export const FindPaginatedListingsForBrandResponseSchema = paginateResponseSchema(FindPaginatedListingsForBrandSchema);

export enum FindPromotedListingsForBrandPaginatedType {
  RECEIVED = 'received',
  SENT = 'sent',
}

export enum FindPaginatedPartnerCommissionsType {
  RECEIVED = 'received',
  PAID = 'paid',
}

export enum PartnerPageTabs {
  EXPERIENCES = 'experiences',
  COMMISSIONS = 'commissions',
}

const FindPromotedListingsForBrandPaginatedQuerySchema = z.object({
  type: z.nativeEnum(FindPromotedListingsForBrandPaginatedType),
  status: z.nativeEnum(PromotedListingStatus).optional(),
  page: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
});

export const FindPromotedListingsForBrandPaginatedEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    partnerBrandId,
    query,
  }: {
    brandId: string;
    partnerBrandId: string;
    query: z.infer<typeof FindPromotedListingsForBrandPaginatedQuerySchema>;
  }) => `v1/brands/${brandId}/partners/${partnerBrandId}/listings?${buildQueryString(query)}`,
  responseSchema: FindPaginatedListingsForBrandResponseSchema,
  querySchema: FindPromotedListingsForBrandPaginatedQuerySchema,
  requestSchema: z.void(),
});

/**
 * "Request experience" agreements resource.
 *
 * Throughout these endpoints, `brandId` is the **promoter** (the requester) and
 * `partnerId` is the **provider** (the brand that owns the experience). The promoter
 * requests to publish a provider's experience on its channel, proposing a commission split.
 */

// Records where an agreement originated. Currently set when created via matchmaking, carrying
// the `matchmaking_recommendation` row that prompted it. Stored as jsonb so it can grow with
// future source kinds without a schema change.
export const PromoterAgreementSourceSchema = z.object({
  recommendationId: z.string().uuid().optional(),
});

const PromoterAgreementSchema = z.object({
  id: z.string().uuid(),
  promoterBrandId: z.string().uuid(),
  providerBrandId: z.string().uuid().nullable(),
  providerOrganizationId: z.string().uuid().nullable(),
  providerListingId: z.string().uuid().nullable(),
  status: z.nativeEnum(PromoterAgreementStatus),
  isPublicChannel: z.boolean(),
  percentCommission: z.number().nullable(),
  flatCommission: z.number().nullable(),
  source: PromoterAgreementSourceSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Promoter's proposed earn % (0-100); the provider keeps the remainder. `recommendationId` is set
// when the request originates from a matchmaking recommendation and is persisted to the agreement's
// `source`.
const RequestPromoterAgreementSchema = z.object({
  percentCommission: z.number().min(0).max(100).optional(),
  recommendationId: z.string().uuid().optional(),
});

// --- Brand-level agreement (the partnership between the two brands) ---

export const GetPromoterAgreementEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, partnerId }: { brandId: string; partnerId: string }) =>
    `v1/brands/${brandId}/promoter-agreements/${partnerId}`,
  responseSchema: PromoterAgreementSchema.nullable(),
  requestSchema: z.void(),
});

const CreatePromoterAgreementForPartnerResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.nativeEnum(PromoterAgreementStatus),
});

export const CreatePromoterAgreementForPartnerEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, partnerId }: { brandId: string; partnerId: string }) =>
    `v1/brands/${brandId}/promoter-agreements/${partnerId}`,
  responseSchema: CreatePromoterAgreementForPartnerResponseSchema,
  requestSchema: RequestPromoterAgreementSchema,
});

// --- Listing-level agreement (a request to publish a specific experience) ---

const GetPromoterAgreementListingResponseSchema = z.object({
  agreement: PromoterAgreementSchema.nullable(),
  providerListingStatus: z.nativeEnum(PromotedListingStatus).nullable(),
  requestToPublish: z.nativeEnum(RequestToPublish),
  defaultCommission: z.number().nullable(),
});

export const GetPromoterAgreementListingEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, partnerId, listingId }: { brandId: string; partnerId: string; listingId: string }) =>
    `v1/brands/${brandId}/promoter-agreements/${partnerId}/listings/${listingId}`,
  responseSchema: GetPromoterAgreementListingResponseSchema.nullable(),
  requestSchema: z.void(),
});

const CreatePromoterAgreementListingResponseSchema = z.object({
  agreementId: z.string().uuid(),
  promotedListingId: z.string().uuid().nullable(),
  status: z.nativeEnum(PromotedListingStatus),
});

export const CreatePromoterAgreementListingEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, partnerId, listingId }: { brandId: string; partnerId: string; listingId: string }) =>
    `v1/brands/${brandId}/promoter-agreements/${partnerId}/listings/${listingId}`,
  responseSchema: CreatePromoterAgreementListingResponseSchema,
  requestSchema: RequestPromoterAgreementSchema,
});
